/**
 * 감사 로그 상세 모달
 * - 목록의 logSeq 클릭 시 표시
 * - 요청/응답 본문, 헤더, 스택트레이스 등 전체 정보를 보여줌
 * - 본문은 JSON 으로 보일 수 있도록 가능한 경우 파싱하여 prettify
 */

import { useMemo, useState } from 'react';
import { Copy, Check } from 'lucide-react';
import { Modal } from '@/components/ui';
import { Section, DescList, StatusBadge } from '@/components/common';
import { LoadingState, ErrorState } from '@/components/common/States';
import { useAuditLogDetail } from '@/hooks/useAuditLogs';
import type { ApiActorType } from '@/services/api';

const ACTOR_LABEL: Record<ApiActorType, string> = {
  GUARDIAN: '보호자',
  TEACHER: '선생님',
  BUSINESS: '사업주',
  ADMIN: '어드민',
  ANONYMOUS: '비로그인',
};

/**
 * JSON 문자열인 경우 들여쓰기, 아니면 원문 반환
 */
function prettifyJson(raw: string | null | undefined): string {
  if (!raw) return '';
  const trimmed = raw.trim();
  if (!trimmed) return '';
  if (
    (trimmed.startsWith('{') && trimmed.endsWith('}')) ||
    (trimmed.startsWith('[') && trimmed.endsWith(']'))
  ) {
    try {
      return JSON.stringify(JSON.parse(trimmed), null, 2);
    } catch {
      return raw;
    }
  }
  return raw;
}

interface CodeBlockProps {
  title: string;
  body: string;
  language?: 'json' | 'text';
  emptyMessage?: string;
}

/**
 * 코드/본문 영역 (복사 버튼 포함)
 */
function CodeBlock({
  title,
  body,
  emptyMessage = '데이터 없음',
}: CodeBlockProps) {
  const [copied, setCopied] = useState(false);
  const isEmpty = !body || body.trim().length === 0;

  const handleCopy = async () => {
    if (isEmpty) return;
    try {
      await navigator.clipboard.writeText(body);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      // ignore
    }
  };

  return (
    <Section
      title={title}
      noPadding
      right={
        !isEmpty && (
          <button
            type="button"
            onClick={handleCopy}
            className="inline-flex items-center gap-1 rounded-md border border-gray-300 bg-white px-2 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50"
          >
            {copied ? (
              <>
                <Check size={12} /> 복사됨
              </>
            ) : (
              <>
                <Copy size={12} /> 복사
              </>
            )}
          </button>
        )
      }
    >
      {isEmpty ? (
        <div className="p-4 text-xs text-gray-400">{emptyMessage}</div>
      ) : (
        <pre className="max-h-72 overflow-auto whitespace-pre-wrap break-all bg-gray-50 p-3 text-xs leading-relaxed text-gray-800">
          {body}
        </pre>
      )}
    </Section>
  );
}

interface AuditLogDetailModalProps {
  logSeq: number | null;
  onClose: () => void;
}

/**
 * 감사 로그 상세 모달
 */
export function AuditLogDetailModal({
  logSeq,
  onClose,
}: AuditLogDetailModalProps) {
  const isOpen = logSeq != null;
  const { data, isLoading, error } = useAuditLogDetail(logSeq);

  const headersPretty = useMemo(
    () => prettifyJson(data?.requestHeaders),
    [data?.requestHeaders],
  );
  const requestBodyPretty = useMemo(
    () => prettifyJson(data?.requestBody),
    [data?.requestBody],
  );
  const responseBodyPretty = useMemo(
    () => prettifyJson(data?.responseBody),
    [data?.responseBody],
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`감사 로그 상세 ${logSeq != null ? `#${logSeq}` : ''}`}
      size="xl"
    >
      {isLoading && <LoadingState />}
      {error && <ErrorState error={error} />}
      {!isLoading && !error && !data && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
          해당 로그를 찾을 수 없습니다 (logSeq=#{logSeq}).
        </div>
      )}

      {data && (
        <div className="max-h-[75vh] space-y-4 overflow-y-auto pr-1">
          {/* v1.4 — apiSummary 헤더 배너
              어드민이 URL만 봐서는 무슨 API 인지 알기 어려운 점을 보완.
              apiSummary 가 null 이면 (Operation 미부착 / 404 스캐너 / v1.4 이전 데이터)
              URL 만 단독 노출되도록 fallback. */}
          <div className="rounded-lg border border-gray-200 bg-gradient-to-r from-primary-50 to-white px-4 py-3">
            {data.apiSummary ? (
              <>
                <div className="text-xs font-medium uppercase tracking-wide text-primary-600">
                  API
                </div>
                <div className="mt-0.5 text-base font-semibold text-gray-900">
                  {data.apiSummary}
                </div>
                <div className="mt-1 flex items-center gap-2 text-xs">
                  <StatusBadge
                    tone={
                      data.httpMethod === 'GET'
                        ? 'info'
                        : data.httpMethod === 'DELETE'
                          ? 'danger'
                          : 'primary'
                    }
                  >
                    {data.httpMethod}
                  </StatusBadge>
                  <span className="break-all font-mono text-gray-600">
                    {data.requestUri}
                  </span>
                </div>
              </>
            ) : (
              <div className="flex items-center gap-2">
                <StatusBadge
                  tone={
                    data.httpMethod === 'GET'
                      ? 'info'
                      : data.httpMethod === 'DELETE'
                        ? 'danger'
                        : 'primary'
                  }
                >
                  {data.httpMethod}
                </StatusBadge>
                <span className="break-all font-mono text-sm text-gray-800">
                  {data.requestUri}
                </span>
              </div>
            )}
          </div>

          {/* 기본 정보 */}
          <Section title="기본 정보">
            <DescList
              cols={2}
              items={[
                {
                  label: '상태',
                  value: (
                    <div className="flex items-center gap-2">
                      <StatusBadge
                        tone={data.successYn === 'Y' ? 'success' : 'danger'}
                      >
                        {data.successYn === 'Y' ? '성공' : '실패'}
                      </StatusBadge>
                      <span className="text-sm">
                        HTTP {data.httpStatus} · {data.durationMs}ms
                      </span>
                    </div>
                  ),
                },
                {
                  label: 'URI 패턴',
                  value: (
                    <span className="break-all font-mono text-xs text-gray-700">
                      {data.uriPattern || '-'}
                    </span>
                  ),
                },
                {
                  label: '쿼리 스트링',
                  value: (
                    <span className="break-all font-mono text-xs text-gray-700">
                      {data.queryString || '-'}
                    </span>
                  ),
                },
                {
                  label: 'Trace ID',
                  value: (
                    <span className="break-all font-mono text-xs text-gray-700">
                      {data.traceId}
                    </span>
                  ),
                },
                {
                  label: '핸들러',
                  value: (
                    <span className="break-all font-mono text-xs text-gray-700">
                      {data.handlerMethod || '-'}
                    </span>
                  ),
                },
                {
                  label: '요청 시각',
                  value: data.dtRequest?.replace('T', ' ') ?? '-',
                },
                {
                  label: '응답 시각',
                  value: data.dtResponse?.replace('T', ' ') ?? '-',
                },
                {
                  label: '요청/응답 크기',
                  value: (
                    <span className="text-sm">
                      {data.requestSize ?? '-'}B / {data.responseSize ?? '-'}B
                    </span>
                  ),
                },
                {
                  label: 'Client IP',
                  value: data.clientIp || '-',
                },
              ]}
            />
          </Section>

          {/* 액터/대상 */}
          <Section title="액터·대상">
            <DescList
              cols={2}
              items={[
                {
                  label: '액터 유형',
                  value: (
                    <StatusBadge tone="neutral">
                      {ACTOR_LABEL[data.cdActorType] ?? data.cdActorType}
                    </StatusBadge>
                  ),
                },
                {
                  label: '앱/플랫폼',
                  value: (
                    <span className="text-sm text-gray-800">
                      {data.platform || '-'}
                      {data.appVersion ? ` · v${data.appVersion}` : ''}
                    </span>
                  ),
                },
                {
                  label: '보호자',
                  value: data.userSeq
                    ? `${data.userName ?? '-'} (#${data.userSeq})`
                    : '-',
                },
                {
                  label: '사업자/선생님',
                  value: data.bizUserSeq
                    ? `${data.bizUserName ?? '-'} (#${data.bizUserSeq})`
                    : '-',
                },
                {
                  label: '사업장',
                  value: data.companySeq
                    ? `${data.companyName ?? '-'} (#${data.companySeq})`
                    : '-',
                },
                {
                  label: '펫',
                  value: data.petSeq
                    ? `${data.petName ?? '-'} (#${data.petSeq})`
                    : '-',
                },
                {
                  label: '티켓',
                  value: data.ticketSeq ? `#${data.ticketSeq}` : '-',
                },
                {
                  label: 'User-Agent',
                  value: (
                    <span className="break-all text-xs text-gray-700">
                      {data.userAgent || '-'}
                    </span>
                  ),
                },
              ]}
            />
          </Section>

          {/* 에러 정보 */}
          {data.successYn === 'N' && (
            <Section title="에러 정보">
              <DescList
                cols={1}
                items={[
                  {
                    label: '에러 코드',
                    value: data.errorCode || '-',
                  },
                  {
                    label: '에러 메시지',
                    value: data.errorMessage || '-',
                  },
                  {
                    label: '예외 클래스',
                    value: (
                      <span className="break-all font-mono text-xs text-gray-700">
                        {data.exceptionClass || '-'}
                      </span>
                    ),
                  },
                ]}
              />
            </Section>
          )}

          {/* 본문/헤더 */}
          <CodeBlock
            title="요청 헤더 (민감 헤더는 ***MASKED***)"
            body={headersPretty}
          />
          <CodeBlock title="요청 본문" body={requestBodyPretty} />
          <CodeBlock title="응답 본문" body={responseBodyPretty} />

          {/* 스택 트레이스 */}
          {data.errorStack && (
            <CodeBlock
              title="스택 트레이스 (상위 30줄)"
              body={data.errorStack}
            />
          )}
        </div>
      )}
    </Modal>
  );
}
